import config from "../config/AdjustWeb";

let sdkPromise;
let sdkChannel;

const readPersist = () => {
  try {
    return JSON.parse(localStorage.getItem("persist") || "{}") || {};
  } catch (error) {
    return {};
  }
};

export const GetAdjustWebChannel = () => {
  if (typeof window === "undefined") return "";
  if (window.JSBridge || window.JSInterface || window.SuWin8Bridge ||
      /GameApp|SKGames|AndroidApp/.test(window.navigator.userAgent) || localStorage.getItem("fromApp")) {
    return "";
  }
  const queryChannel = new URLSearchParams(window.location.search).get("c");
  const pathChannel = window.location.pathname.startsWith("/c/") ? window.location.pathname.substring(3) : "";
  const channel = queryChannel || pathChannel || readPersist().channel;
  return Object.prototype.hasOwnProperty.call(config.channels, channel) ? channel : "";
};

export const IsAdjustWebChannel = () => Boolean(GetAdjustWebChannel());

const saveAttribution = (sdk, attribution, channel) => {
  if (GetAdjustWebChannel() !== channel || !attribution || !attribution.adid) return;
  const previous = localStorage.getItem("attribution");
  // 保留其他渠道已有归因；允许本次 Web 归因回调补全推广信息。
  if (previous && !(previous === "adjust" &&
      localStorage.getItem(`adjust_${channel}_adid`) === localStorage.getItem("attribution_id"))) return;
  const source = { ...attribution, channel, platform: "web" };
  const webUUID = sdk.getWebUUID();
  if (webUUID) source.web_uuid = webUUID;
  localStorage.setItem("attribution", "adjust");
  localStorage.setItem("attribution_id", attribution.adid);
  localStorage.setItem("ad_source", JSON.stringify(source));
  localStorage.setItem(`adjust_${channel}_adid`, attribution.adid);
};

export const InitAdjustWeb = () => {
  const channel = GetAdjustWebChannel();
  if (!channel) return Promise.resolve(null);
  // SDK 在当前页面只初始化一次，避免渠道切换时复用另一个 tracker。
  if (sdkPromise && sdkChannel !== channel) return Promise.resolve(null);
  if (!sdkPromise) {
    sdkChannel = channel;
    sdkPromise = import("@adjustcom/adjust-web-sdk").then((module) => {
      if (GetAdjustWebChannel() !== channel) {
        sdkPromise = null;
        return null;
      }
      const sdk = module.default || module;
      const env = process.env.PREACT_APP_ENV_CONFIG || {};
      const sandbox = process.env.NODE_ENV !== "production" || env.BUILD_FOR === "TEST" || env.BUILD_FOR === "WIFI_TEST";
      const options = {
        appToken: config.appToken,
        environment: sandbox ? "sandbox" : "production",
        logLevel: sandbox ? "verbose" : "error",
        namespace: channel,
        attributionCallback: (event, attribution) => saveAttribution(sdk, attribution, channel),
      };
      options.defaultTracker = config.channels[channel];
      sdk.initSdk(options);
      const user = readPersist().user_info;
      if (user && user.rid) sdk.addGlobalPartnerParameters([{ key: "user_id", value: String(user.rid) }]);
      sdk.waitForAttribution().then((attribution) => saveAttribution(sdk, attribution, channel)).catch(reportError);
      sdk.waitForWebUUID().then(() => saveAttribution(sdk, sdk.getAttribution(), channel)).catch(reportError);
      return sdk;
    }).catch((error) => {
      sdkPromise = null;
      reportError(error);
      return null;
    });
  }
  return sdkPromise;
};

const reportError = (error) => console.warn("[Adjust H5]", error);
const textValue = (value) => value == null ? "" : String(value).trim();

const track = (sdk, eventToken, data, requireRevenue, suffix = "") => {
  const revenue = Number(data.revenue == null ? data.value : data.revenue);
  const currency = textValue(data.currency).toUpperCase();
  const hasRevenue = Number.isFinite(revenue) && revenue > 0 && /^[A-Z]{3}$/.test(currency);
  if (requireRevenue && !hasRevenue) return Promise.resolve();
  const event = { eventToken, partnerParams: [] };
  const transactionId = textValue(data.transactionId || data.transaction_id);
  const userId = textValue(data.rid || data.user_id);
  if (transactionId) {
    event.deduplicationId = transactionId + suffix;
    event.partnerParams.push({ key: "transaction_id", value: transactionId });
  }
  if (userId) event.partnerParams.push({ key: "user_id", value: userId });
  if ((requireRevenue || suffix) && hasRevenue) {
    event.revenue = revenue;
    event.currency = currency;
  }
  return sdk.trackEvent(event);
};

export const SendAdjustWebEvent = (event, data = {}) => {
  const name = textValue(event).toLowerCase();
  // PP 的注册转化仅计正式账号，游客注册不能占用唯一注册事件。
  if (name === "register2" && GetAdjustWebChannel() === "pp") return Promise.resolve();
  if (!["register", "register2", "recharge", "purchase", "first_purchase", "firstpurchase"].includes(name)) {
    return Promise.resolve();
  }
  return InitAdjustWeb().then((sdk) => {
    if (!sdk || GetAdjustWebChannel() !== sdkChannel) return;
    if (name === "register" || name === "register2") return track(sdk, config.registerToken, data, false);
    if (name === "first_purchase" || name === "firstpurchase") {
      return track(sdk, config.firstPurchaseToken, data, false, "_first_purchase");
    }
    const events = [track(sdk, config.purchaseToken, data, true)];
    if (data.isFirstPurchase === true || data.is_first_purchase === true) {
      events.push(track(sdk, config.firstPurchaseToken, data, true, "_first_purchase"));
    }
    return Promise.all(events);
  }).catch(reportError);
};

export const BindAdjustWebUser = (rid) => {
  if (!textValue(rid)) return Promise.resolve();
  return InitAdjustWeb().then((sdk) => {
    if (sdk && GetAdjustWebChannel() === sdkChannel) sdk.addGlobalPartnerParameters([{ key: "user_id", value: textValue(rid) }]);
  }).catch(reportError);
};
